import React, { useState } from 'react';

interface StarRatingProps {
  rating: number;
  maxStars?: number;
  size?: string;
  interactive?: boolean;
  onChange?: (rating: number) => void;
  label?: string;
}

const StarRating: React.FC<StarRatingProps> = ({
  rating,
  maxStars = 5,
  size = 'w-4 h-4',
  interactive = false,
  onChange,
  label,
}) => {
  const [hoverRating, setHoverRating] = useState(0);

  const ratingLabels: Record<number, string> = {
    1: 'Poor',
    2: 'Fair',
    3: 'Good',
    4: 'Very Good',
    5: 'Excellent',
  };

  return (
    <div className="flex items-center gap-1" role="group" aria-label={label || `Rating: ${rating} out of ${maxStars} stars`}>
      {Array.from({ length: maxStars }, (_, i) => i + 1).map((star) => {
        const filled = star <= (hoverRating || rating);
        return (
          <button
            key={star}
            type="button"
            disabled={!interactive}
            onClick={() => interactive && onChange?.(star)}
            onMouseEnter={() => interactive && setHoverRating(star)}
            onMouseLeave={() => interactive && setHoverRating(0)}
            className={`${interactive ? 'cursor-pointer' : 'cursor-default'} transition-colors duration-200 ${
              filled ? 'text-amber-500' : 'text-stone-200'
            }`}
            aria-label={`${star} star${star !== 1 ? 's' : ''} - ${ratingLabels[star] || ''}`}
          >
            <svg
              className={`${size} ${filled ? 'fill-current' : ''}`}
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1"
              aria-hidden="true"
            >
              <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
            </svg>
          </button>
        );
      })}
      {interactive && hoverRating > 0 && (
        <span className="text-[10px] font-bold text-stone-400 uppercase tracking-widest ml-2" aria-live="polite">
          {ratingLabels[hoverRating]}
        </span>
      )}
    </div>
  );
};

export default StarRating;
